import { ExportButton } from '@/components/exports/ExportButton';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPanel } from '@/components/ui/filter-panel';
import type { PayoutBatchesFilters as PayoutBatchesQueryFilters } from '@/hooks/queries/use-payout-batches-query';
import { usePayoutBatchesQuery } from '@/hooks/queries/use-payout-batches-query';
import { Box } from '@chakra-ui/react';

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GlobalPayoutDrawer from '../../payouts/details/GlobalPayoutDrawer';
import { PayoutBatchesFilters } from '../../payouts/PayoutBatchesFilters';
import { PayoutBatchesTable } from '../../payouts/PayoutBatchesTable';
import { DownloadDataWindow, MoneySquare, Search, WarningTriangle } from 'iconoir-react';

const DEFAULT_LIMIT = 15;
const EMPTY_ICON_SIZE = 48;
const EMPTY_ICON_STROKE = 1.8;

export const PayoutsTabContent: React.FC = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<PayoutBatchesQueryFilters>({});
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Fetch data
  const {
    data: payoutData,
    isLoading,
    isError,
    // refetch, // Can be used to manually trigger a refetch
  } = usePayoutBatchesQuery({
    ...filters,
    page: currentPage,
    limit: DEFAULT_LIMIT,
  });

  const handleFiltersChange = useCallback((newFilters: PayoutBatchesQueryFilters) => {
    setFilters(newFilters);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const payouts = payoutData?.data ?? [];

  // `PayoutBatchesFilters` only reports the filters the user actually set, so an
  // empty object means the list is unfiltered and a zero result is a real "no
  // payouts yet" rather than "nothing matches".
  const hasActiveFilters = Object.keys(filters).length > 0;
  const isEmpty = !isLoading && !isError && payouts.length === 0;

  // With no payout at all the filter bar and the export button have nothing to
  // act on, so they come off screen with the table.
  const showFilterBar = !isEmpty || hasActiveFilters;

  return (
    <Box>
      {showFilterBar && (
        <FilterPanel
          mb={5}
          actionsSlot={
            <ExportButton
              resource="payout_batches"
              filters={filters as Record<string, unknown>}
              label={t('exports.payouts.action')}
              size="sm"
              variant="outline"
              colorPalette="gray"
              leftIcon={<DownloadDataWindow width={16} height={16} />}
            />
          }
        >
          <PayoutBatchesFilters onFiltersChange={handleFiltersChange} initialFilters={filters} />
        </FilterPanel>
      )}
      {isError ? (
        <EmptyState
          title={t('commissions.payouts.errorLoading')}
          icon={<WarningTriangle width={22} height={22} />}
        />
      ) : isEmpty ? (
        <EmptyState
          icon={
            hasActiveFilters ? (
              <Search
                width={EMPTY_ICON_SIZE}
                height={EMPTY_ICON_SIZE}
                strokeWidth={EMPTY_ICON_STROKE}
              />
            ) : (
              <MoneySquare
                width={EMPTY_ICON_SIZE}
                height={EMPTY_ICON_SIZE}
                strokeWidth={EMPTY_ICON_STROKE}
              />
            )
          }
          title={t(
            hasActiveFilters ? 'commissions.payouts.noResults' : 'commissions.payouts.empty'
          )}
          description={t(
            hasActiveFilters
              ? 'commissions.payouts.noResultsDescription'
              : 'commissions.payouts.emptyDescription'
          )}
        />
      ) : (
        <PayoutBatchesTable
          payouts={payouts}
          pagination={payoutData?.meta}
          loading={isLoading}
          onPageChange={handlePageChange}
        />
      )}
      {/* Payout details drawer using nuqs */}
      <GlobalPayoutDrawer />
    </Box>
  );
};

export default PayoutsTabContent;
